import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  FeedbackPayload,
  FeedbackResponse,
  SavedSearch,
  CreateSavedSearchParams,
} from '@/types';
import { getAccessToken } from '@/utils/jwt';

const AGENT_MCP_URL = import.meta.env.VITE_AGENT_MCP_URL as string | undefined;

export const feedbackApi = createApi({
  reducerPath: 'feedbackApi',
  baseQuery: fetchBaseQuery({
    baseUrl: AGENT_MCP_URL ?? '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Feedback', 'SavedSearch'],
  endpoints: (builder) => ({
    submitFeedback: builder.mutation<FeedbackResponse, FeedbackPayload>({
      query: (payload) => ({
        url: '/feedback',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Feedback'],
    }),

    getFeedbackForMessage: builder.query<FeedbackResponse[], string>({
      query: (messageId) => `/feedback/${messageId}`,
      providesTags: ['Feedback'],
    }),

    listSavedSearches: builder.query<SavedSearch[], undefined>({
      query: () => '/saved-searches',
      providesTags: ['SavedSearch'],
    }),

    createSavedSearch: builder.mutation<SavedSearch, CreateSavedSearchParams>({
      query: (params) => ({
        url: '/saved-searches',
        method: 'POST',
        body: params,
      }),
      invalidatesTags: ['SavedSearch'],
    }),

    deleteSavedSearch: builder.mutation<undefined, string>({
      query: (id) => ({
        url: `/saved-searches/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SavedSearch'],
    }),
  }),
});

export const {
  useSubmitFeedbackMutation,
  useGetFeedbackForMessageQuery,
  useListSavedSearchesQuery,
  useCreateSavedSearchMutation,
  useDeleteSavedSearchMutation,
} = feedbackApi;
